import { Link } from "react-router-dom";
import { Phone, ArrowUpRight, Check, Clock } from "lucide-react";
import { COMPANY, SERVICES, IMAGES, HOURS } from "@/content";
import CallLink from "@/components/CallLink";
import { Reveal } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Services() {
  usePageMeta("Our Services — A/C Maintenance, Repair, Inspections, Duct Cleaning & Air Quality", "A/C maintenance, repair & replacement, new-home inspections, duct & dryer vent cleaning and REME HALO indoor air quality in Fort Myers, Estero, Naples & Bonita Springs, FL.");
  return (
    <>
      <section data-testid="services-hero" className="relative pt-40 pb-20 md:pt-52 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.replacement} alt="A/C services" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-bone via-bone/85 to-charcoal/30" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10">
          <p className="text-terracotta text-xs tracking-[0.3em] uppercase font-semibold mb-5">Residential & Commercial</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tight text-charcoal max-w-4xl leading-[0.95]">
            Complete air conditioning services, done honestly.
          </h1>
          <p className="mt-6 max-w-xl text-ink text-lg leading-relaxed">
            From routine tune-ups to full system installs and cleaner indoor air — {COMPANY.full} handles it all across Fort Myers, Estero, Naples and Bonita Springs.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-10 pb-10">
        {SERVICES.map((s, i) => (
          <Reveal key={s.slug}>
            <article id={s.slug} data-testid={`service-detail-${s.slug}`}
              className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center py-16 md:py-24 border-t border-sand ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div className="relative">
                <img src={s.image} alt={s.title} className="rounded-3xl w-full h-[360px] md:h-[460px] object-cover" />
                <span className="absolute top-5 left-5 font-heading text-6xl text-bone drop-shadow-lg">0{i + 1}</span>
              </div>
              <div>
                <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-charcoal leading-[1]">
                  <Link to={`/services/${s.path}`} className="hover:text-terracotta transition-colors duration-200">{s.title}</Link>
                </h2>
                <p className="mt-5 text-ink leading-relaxed">{s.body}</p>
                <ul className="mt-7 space-y-3">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-sage/15 text-sage shrink-0"><Check size={14} /></span>
                      <span className="text-charcoal">{p}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Link to={`/services/${s.path}`} data-testid={`service-learn-more-${s.slug}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-3.5 font-semibold text-bone hover:bg-terracotta-dark transition-colors duration-200">
                    Learn more <ArrowUpRight size={17} />
                  </Link>
                  <a href="#quote" data-testid={`service-cta-${s.slug}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-charcoal/20 px-7 py-3.5 font-semibold text-charcoal hover:bg-charcoal hover:text-bone transition-colors duration-200">
                    Request this service
                  </a>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      <section id="quote" className="bg-sand/50 py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <Reveal>
            <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-5">Get started</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-charcoal leading-[0.98]">Request your free quote.</h2>
            <p className="mt-6 text-ink leading-relaxed max-w-md">Pick a service, tell us a little about your system, and we'll follow up quickly with honest advice.</p>
            <CallLink className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-sage text-bone px-6 py-5 hover:bg-sage/90 transition-colors duration-200">
              <span className="grid place-items-center h-11 w-11 rounded-full bg-bone/15"><Phone size={20} /></span>
              <span><span className="block text-xs uppercase tracking-widest text-bone/70">Call us today</span><span className="font-heading text-2xl">{COMPANY.phone}</span></span>
            </CallLink>
            <div data-testid="business-hours" className="mt-4 flex w-fit items-center gap-2.5 rounded-full border border-sand bg-white px-4 py-2 text-sm text-charcoal">
              <Clock size={15} className="text-terracotta" />
              <span><span className="font-semibold">{HOURS.days}</span> · {HOURS.time}</span>
            </div>
            <Link to="/" className="mt-6 block text-terracotta font-semibold hover:underline">← Back to home</Link>
          </Reveal>
          <Reveal delay={0.1}><ContactForm /></Reveal>
        </div>
      </section>
    </>
  );
}
